import axios from '../../api-client.ts';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import {KTCard, KTCardBody, KTIcon, KTSVG} from '../../../_mesomb/helpers';
import LoadingSpinner from '../../../_mesomb/partials/components/LoadingSpinner.tsx';
import { SecureTransactionModel } from '../../../types';
import SecureTransaction from './components/SecureTransaction.tsx';

function normalizeSecureTransaction(txn: SecureTransactionModel): SecureTransactionModel {
  const feeAmount = txn.fee_amount ?? txn.fees ?? '0';
  const computedNet = Number.isFinite(Number(txn.amount)) && Number.isFinite(Number(feeAmount))
    ? (Number(txn.amount) - Number(feeAmount)).toFixed(2)
    : '0';

  return {
    ...txn,
    merchant_reference: txn.merchant_reference ?? txn.order ?? txn.external_id ?? txn.fin_trx_id ?? txn.id,
    description: txn.description ?? txn.message ?? '',
    fee_amount: feeAmount,
    net_amount: txn.net_amount ?? computedNet,
    fee_strategy: txn.fee_strategy ?? 'MERCHANT_PAYS',
    release_rule: txn.release_rule ?? 'BUYER_CONFIRMATION',
    auto_release_delay_hours: txn.auto_release_delay_hours ?? null,
    dispute_status: txn.dispute_status ?? 'NONE',
    frozen_by_admin: txn.frozen_by_admin ?? false,
    frozen_reason: txn.frozen_reason ?? null,
    review_status: txn.review_status ?? 'CLEAR',
    risk_score: txn.risk_score ?? 0,
    risk_flags: txn.risk_flags ?? [],
    metadata: txn.metadata ?? {},
    confirmation_url: txn.confirmation_url ?? '',
    audit_logs: txn.audit_logs ?? [],
    ledger_entries: txn.ledger_entries ?? [],
    disputes: txn.disputes ?? [],
    webhook_deliveries: txn.webhook_deliveries ?? [],
    created_at: txn.created_at ?? txn.ts ?? new Date().toISOString(),
    updated_at: txn.updated_at ?? txn.ts ?? new Date().toISOString(),
  };
}

function SecurePaymentPage() {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [transaction, setTransaction] = useState<SecureTransactionModel | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const load = useCallback(() => {
    if (!token) return;
    setLoading(true);
    axios
      .get<SecureTransactionModel>(`/secure-pay/${token}`)
      .then(({ data }) => {
        setTransaction(normalizeSecureTransaction(data));
        setError(undefined);
      })
      .catch(() => {
        setError('Transaction not found or the link has expired.');
        setTransaction(undefined);
      })
      .finally(() => setLoading(false));
  }, [token]);
  useEffect(() => {
    load();
  }, [load]);
  if (loading) return <LoadingSpinner />;
  if (error || !transaction) {
    return (
      <div className='d-flex flex-column flex-root min-vh-100'>
        <div className='d-flex flex-column flex-center flex-column-fluid'>
          <KTCard flush={true} className='w-lg-500px'>
            <KTCardBody className='py-15 text-center'>
              <div className='symbol symbol-60px symbol-circle bg-light-danger mx-auto mb-5'>
                <span className='symbol-label'>
                  <KTSVG path={'/assets/icons/shield-tick.svg'} className='svg-icon-1 svg-icon-danger' />
                </span>
              </div>
              <h3 className='mb-3'>Transaction unavailable</h3>
              <p className='text-muted mb-0'>{error}</p>
            </KTCardBody>
          </KTCard>
        </div>
      </div>
    );
  }
  return (
    <div className='d-flex flex-column flex-root'>
      <div className='d-flex flex-column flex-center flex-column-fluid'>
        <div className='d-flex flex-column p-10'>
          <SecureTransaction transaction={transaction} token={token!} onRefresh={load} />
        </div>
      </div>
    </div>
  );
}
export default SecurePaymentPage;
